import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ArrowLeft, Activity, Plane, Sprout, AlertTriangle, CheckCircle2 } from 'lucide-react'
import { getCycleProgress, getPilotProjects } from '../../services/api'

function ProgressBar({ value, target, color = 'green' }) {
  const pct = target > 0 ? Math.min(100, Math.round((value / target) * 100)) : 0
  const colorMap = {
    green: 'bg-green-500',
    blue: 'bg-blue-500',
    amber: 'bg-amber-500',
    red: 'bg-red-500',
  }
  const barColor = pct < 33 ? colorMap.red : pct < 66 ? colorMap.amber : colorMap[color]
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-gray-600">
        <span>{value} / {target}</span>
        <span className="font-medium">{pct}%</span>
      </div>
      <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

export default function CycleProgress({ ranchId }) {
  const [pilotId, setPilotId] = useState('')

  const { data: pilots } = useQuery({
    queryKey: ['pilotProjects', ranchId],
    queryFn: () => getPilotProjects(ranchId),
  })

  const { data: progress, isLoading, error } = useQuery({
    queryKey: ['cycleProgress', ranchId, pilotId],
    queryFn: () => getCycleProgress(pilotId
      ? { ranch_id: ranchId, pilot_id: pilotId }
      : { ranch_id: ranchId }),
    enabled: Boolean(pilotId) || (pilots && pilots.length === 0),
  })

  // Auto-select the first pilot once the list resolves. useEffect, not
  // setState-in-render, so we don't loop.
  useEffect(() => {
    if (!pilotId && pilots && pilots.length > 0) {
      setPilotId(pilots[0].id)
    }
  }, [pilotId, pilots])

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/land" className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="h-5 w-5 text-gray-500" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cycle Progress</h1>
          <p className="text-gray-500 mt-1">
            Per-pilot-cycle progress against drone-operator §6 targets
          </p>
        </div>
      </div>

      {pilots && pilots.length > 1 && (
        <div className="card p-4">
          <label className="label">Pilot Cycle</label>
          <select
            className="input w-full md:w-1/2"
            value={pilotId}
            onChange={e => setPilotId(e.target.value)}
          >
            <option value="">Select a pilot</option>
            {pilots.map(p => (
              <option key={p.id} value={p.id}>{p.title} — {p.grant_ref || p.grantor}</option>
            ))}
          </select>
        </div>
      )}

      {isLoading && (
        <div className="card p-8 text-center text-gray-400">Loading…</div>
      )}

      {error && (
        <div className="card p-6 flex items-center gap-2 text-red-700 bg-red-50">
          <AlertTriangle className="h-5 w-5" />
          {error.response?.data?.detail || 'Failed to load cycle progress.'}
        </div>
      )}

      {progress && (
        <>
          <div className="card p-4">
            <div className="text-sm text-gray-500">
              Window: {progress.start_date} → {progress.end_date}
              {progress.pilot_id ? ' • pilot scope' : ' • ad-hoc range'}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="card p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2.5 rounded-xl bg-blue-50 ring-1 ring-blue-200">
                  <Plane className="h-5 w-5 text-blue-600" />
                </div>
              </div>
              <div className="text-sm text-gray-500">Drone Flights</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">
                {progress.flights_total}
                <span className="text-sm font-normal text-gray-400 ml-1">
                  / {progress.flights_target}
                </span>
              </div>
              <div className="mt-3">
                <ProgressBar value={progress.flights_total} target={progress.flights_target} color="blue" />
              </div>
            </div>

            <div className="card p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2.5 rounded-xl bg-green-50 ring-1 ring-green-200">
                  <Sprout className="h-5 w-5 text-green-600" />
                </div>
              </div>
              <div className="text-sm text-gray-500">Grass Quadrats</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">
                {progress.grass_quadrats_total}
                <span className="text-sm font-normal text-gray-400 ml-1">
                  / {progress.grass_quadrats_target}
                </span>
              </div>
              <div className="mt-3">
                <ProgressBar value={progress.grass_quadrats_total} target={progress.grass_quadrats_target} color="green" />
              </div>
            </div>

            <div className="card p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2.5 rounded-xl bg-amber-50 ring-1 ring-amber-200">
                  <Activity className="h-5 w-5 text-amber-600" />
                </div>
              </div>
              <div className="text-sm text-gray-500">Lantana Quadrats</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">
                {progress.lantana_quadrats_total}
                <span className="text-sm font-normal text-gray-400 ml-1">
                  / {progress.lantana_quadrats_target}
                </span>
              </div>
              <div className="mt-3">
                <ProgressBar value={progress.lantana_quadrats_total} target={progress.lantana_quadrats_target} color="amber" />
              </div>
            </div>
          </div>

          <div className="card p-5">
            <div className="flex items-center gap-2 mb-2">
              {progress.in_window_pct >= 0.5
                ? <CheckCircle2 className="h-5 w-5 text-green-600" />
                : <AlertTriangle className="h-5 w-5 text-amber-600" />}
              <h3 className="text-sm font-semibold text-gray-900">3-Day Linkage Compliance</h3>
            </div>
            <p className="text-sm text-gray-500 mb-3">
              {progress.in_window_quadrats} of {progress.in_window_quadrats + progress.out_of_window_quadrats}
              {' '}quadrats taken within ±3 days of a drone flight covering the same paddock
              ({Math.round(progress.in_window_pct * 100)}%; target ≥50%).
            </p>
            <ProgressBar
              value={progress.in_window_quadrats}
              target={progress.in_window_quadrats + progress.out_of_window_quadrats || 1}
              color="green"
            />
          </div>

          <div className="card overflow-hidden">
            <div className="p-4 border-b">
              <h3 className="text-sm font-semibold text-gray-900">Per-Paddock Coverage</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Paddock</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Flights</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Grass Quadrats</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Lantana Quadrats</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">In-Window</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Classes ≥5 polygons</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {progress.paddocks.map(p => (
                    <tr key={p.paddock_id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm font-medium">{p.paddock_code}</td>
                      <td className="px-4 py-2 text-sm text-right">{p.flights_count}</td>
                      <td className="px-4 py-2 text-sm text-right">
                        <span className={p.grass_quadrats_count >= 3 ? 'text-green-700' : 'text-amber-700'}>
                          {p.grass_quadrats_count}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm text-right">{p.lantana_quadrats_count}</td>
                      <td className="px-4 py-2 text-sm text-right">{p.in_window_quadrats} / {p.total_quadrats}</td>
                      <td className="px-4 py-2 text-sm text-right">{p.polygon_classes_at_target}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
